import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortal, API_BASE } from './HousePortalLayout';

const T = {
  en: {
    title: 'Virtual Tour',
    loading: 'Loading virtual tour...',
    livingRoom: 'Living Room', bedroom: 'Master Bedroom', kitchen: 'Kitchen',
    bathroom: 'Bathroom', balcony: 'Balcony & View', lobby: 'Building Lobby',
    pool: 'Swimming Pool', gym: 'Fitness Center',
    askAbout: 'Ask About This Space',
    q1: "What's the view like from here?",
    q2: "How's the natural light?",
    q3: 'What are the dimensions?',
    q4: 'What materials are used?',
    chatPlaceholder: 'Ask about this room...',
    send: 'Send',
    interested: 'Interested? Schedule a Viewing',
    schedule: 'Schedule a Viewing',
    viewProperty: 'View Full Property Details',
    progress: 'Tour Progress',
    beds: 'Beds', baths: 'Baths', sqm: 'sqm',
    name: 'Full Name', email: 'Email', phone: 'Phone',
    submit: 'Request Viewing',
    submitted: 'Request Sent!',
    leadCapture: 'Enter your details to unlock the full tour experience',
    continue: 'Continue Tour',
    back: 'Back to Property',
    photoOf: 'Photo',
    aiTourGuide: 'AI Tour Guide',
    clickToZoom: 'Click image to enlarge',
    noTour: 'Tour is being prepared...',
  },
  th: {
    title: 'ทัวร์เสมือนจริง',
    loading: 'กำลังโหลดทัวร์เสมือนจริง...',
    livingRoom: 'ห้องนั่งเล่น', bedroom: 'ห้องนอนใหญ่', kitchen: 'ห้องครัว',
    bathroom: 'ห้องน้ำ', balcony: 'ระเบียง & วิว', lobby: 'ล็อบบี้',
    pool: 'สระว่ายน้ำ', gym: 'ฟิตเนส',
    askAbout: 'ถามเกี่ยวกับพื้นที่นี้',
    q1: 'วิวจากที่นี่เป็นอย่างไร?',
    q2: 'แสงธรรมชาติเป็นอย่างไร?',
    q3: 'ขนาดเท่าไร?',
    q4: 'ใช้วัสดุอะไร?',
    chatPlaceholder: 'ถามเกี่ยวกับห้องนี้...',
    send: 'ส่ง',
    interested: 'สนใจ? นัดหมายดูห้อง',
    schedule: 'นัดหมายดูห้อง',
    viewProperty: 'ดูรายละเอียดทั้งหมด',
    progress: 'ความคืบหน้าทัวร์',
    beds: 'ห้องนอน', baths: 'ห้องน้ำ', sqm: 'ตร.ม.',
    name: 'ชื่อ', email: 'อีเมล', phone: 'โทรศัพท์',
    submit: 'ขอนัดหมายดูห้อง',
    submitted: 'ส่งแล้ว!',
    leadCapture: 'กรอกข้อมูลเพื่อปลดล็อคประสบการณ์ทัวร์เต็มรูปแบบ',
    continue: 'ทัวร์ต่อ',
    back: 'กลับไปอสังหาริมทรัพย์',
    photoOf: 'ภาพ',
    aiTourGuide: 'AI ไกด์ทัวร์',
    clickToZoom: 'คลิกรูปเพื่อขยาย',
    noTour: 'กำลังเตรียมทัวร์...',
  },
  zh: {
    title: '虛擬導覽',
    loading: '正在載入虛擬導覽...',
    livingRoom: '客廳', bedroom: '主臥室', kitchen: '廚房',
    bathroom: '浴室', balcony: '陽台 & 景觀', lobby: '大廳',
    pool: '游泳池', gym: '健身中心',
    askAbout: '詢問此空間',
    q1: '這裡的景觀如何？',
    q2: '自然光照如何？',
    q3: '尺寸是多少？',
    q4: '使用了什麼材料？',
    chatPlaceholder: '詢問此房間...',
    send: '發送',
    interested: '有興趣？預約看房',
    schedule: '預約看房',
    viewProperty: '查看完整房產詳情',
    progress: '導覽進度',
    beds: '臥室', baths: '浴室', sqm: '平方米',
    name: '姓名', email: '電子郵件', phone: '電話',
    submit: '申請看房',
    submitted: '已提交！',
    leadCapture: '輸入您的詳細信息以解鎖完整導覽體驗',
    continue: '繼續導覽',
    back: '返回房產',
    photoOf: '照片',
    aiTourGuide: 'AI 導覽員',
    clickToZoom: '點擊圖片放大',
    noTour: '導覽準備中...',
  },
};

const ROOMS = [
  { key: 'living', icon: '\uD83D\uDECB\uFE0F', emoji: '🛋️' },
  { key: 'bedroom', icon: '\uD83D\uDECF\uFE0F', emoji: '🛏️' },
  { key: 'kitchen', icon: '\uD83C\uDF73', emoji: '🍳' },
  { key: 'bathroom', icon: '\uD83D\uDEBF', emoji: '🚿' },
  { key: 'balcony', icon: '\uD83C\uDF07', emoji: '🌇' },
  { key: 'lobby', icon: '\uD83C\uDFE8', emoji: '🏨' },
  { key: 'pool', icon: '\uD83C\uDFCA', emoji: '🏊' },
  { key: 'gym', icon: '\uD83C\uDFCB\uFE0F', emoji: '🏋️' },
];

const FALLBACK_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #667eea 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #f5576c 0%, #fa709a 100%)',
];

export default function HousePortalTour() {
  const { tourId } = useParams();
  const { slug, lang, isMobile } = usePortal();
  const navigate = useNavigate();
  const t = T[lang] || T.en;

  const [property, setProperty] = useState(null);
  const [tourData, setTourData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRoom, setActiveRoom] = useState(0);
  const [activePhoto, setActivePhoto] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '' });
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (activeRoom >= 3 && !leadSubmitted) setShowLeadCapture(true);
  }, [activeRoom, leadSubmitted]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatSending]);

  useEffect(() => {
    setActivePhoto(0);
  }, [activeRoom]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [propRes, tourRes] = await Promise.all([
          fetch(`${API_BASE}/h/${slug}/properties/${tourId}`),
          fetch(`${API_BASE}/h/${slug}/tours/by-property/${tourId}`),
        ]);
        if (propRes.ok) {
          const propData = await propRes.json();
          setProperty(propData);
          console.log('[Tour] Property loaded:', propData.title);
        } else {
          console.warn('[Tour] Property fetch failed:', propRes.status);
        }
        if (tourRes.ok) {
          const tData = await tourRes.json();
          setTourData(tData);
          const ri = tData.room_images || {};
          console.log('[Tour] Tour loaded, rooms:', Object.keys(ri).length, 'images:', Object.values(ri).flat().length);
        } else {
          console.warn('[Tour] Tour fetch failed:', tourRes.status);
        }
      } catch (e) { console.error('[Tour] Fetch error:', e); }
      setLoading(false);
    };
    fetchData();
  }, [slug, tourId]);

  const proxyUrl = (url) => {
    if (!url) return url;
    if (url.includes('picsum.photos')) {
      return `${API_BASE}/h/${slug}/img-proxy?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  const getRoomImages = (roomKey) => {
    if (tourData?.room_images?.[roomKey]) return tourData.room_images[roomKey].map(proxyUrl);
    return [];
  };

  const sendChat = useCallback(async (msg) => {
    if (!msg.trim() || chatSending) return;
    setChatMessages(prev => [...prev, { role: 'user', content: msg, ts: Date.now() }]);
    setChatInput('');
    setChatSending(true);
    try {
      const roomName = ROOMS[activeRoom]?.key || 'room';
      const res = await fetch(`${API_BASE}/h/${slug}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          property_id: tourId,
          context: `User is viewing the ${roomName} during a virtual tour`,
          conversation_history: chatMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.reply || data.message || 'This is a beautifully designed space.', ts: Date.now() }]);
      } else {
        const fallback = {
          living: 'The living room features floor-to-ceiling windows with stunning views, premium flooring, and a spacious layout perfect for entertaining.',
          bedroom: 'The master bedroom is generously sized with a walk-in closet, ensuite bathroom, and panoramic views.',
          kitchen: 'The kitchen is equipped with premium appliances, marble countertops, and a large island for casual dining.',
          bathroom: 'The bathroom features imported marble, a rain shower, and a freestanding soaking tub.',
          balcony: 'The balcony offers a breathtaking panoramic view. Perfect for morning coffee or evening relaxation.',
          lobby: 'The building lobby features double-height ceilings, contemporary art, and 24/7 concierge service.',
          pool: 'The infinity pool is on the rooftop with spectacular views, including jacuzzi, sun deck, and poolside service.',
          gym: 'The state-of-the-art fitness center features premium equipment, yoga studio, and personal training services.',
        };
        setChatMessages(prev => [...prev, { role: 'assistant', content: fallback[roomName] || 'This is a premium space. Would you like to know more?', ts: Date.now() }]);
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'This is a premium space with high-quality finishes. Let me know your questions!', ts: Date.now() }]);
    } finally {
      setChatSending(false);
    }
  }, [slug, tourId, chatMessages, chatSending, activeRoom]);

  const handleLeadSubmit = async () => {
    try {
      await fetch(`${API_BASE}/h/${slug}/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...leadForm, property_id: parseInt(tourId), source: 'virtual_tour' }),
      });
    } catch { /* ok */ }
    setLeadSubmitted(true);
    setShowLeadCapture(false);
  };

  const roomNames = {
    living: t.livingRoom, bedroom: t.bedroom, kitchen: t.kitchen, bathroom: t.bathroom,
    balcony: t.balcony, lobby: t.lobby, pool: t.pool, gym: t.gym,
  };

  if (loading) {
    return (
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#d4a537',
            animation: 'houseSpinner 1s linear infinite', margin: '0 auto 16px',
          }} />
          <style>{`@keyframes houseSpinner { to { transform: rotate(360deg); } }
@keyframes houseFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes houseTyping { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }`}</style>
          <p style={{ color: 'rgba(255,255,255,0.5)' }}>{t.loading}</p>
        </div>
      </div>
    );
  }

  const p = property || { title: 'Luxury Property', price: 0, bedrooms: 0, bathrooms: 0, area_sqm: 0 };
  const progress = ((activeRoom + 1) / ROOMS.length) * 100;
  const currentRoom = ROOMS[activeRoom];
  const currentImages = getRoomImages(currentRoom?.key);
  const currentImg = currentImages[activePhoto] || null;

  const formatPrice = (price, currency = 'THB') => {
    if (!price) return '';
    return `${currency} ${price.toLocaleString()}`;
  };

  return (
    <div style={{ background: '#0d0d1a', minHeight: 'calc(100vh - 72px)' }}>
      <style>{`
@keyframes houseSpinner { to { transform: rotate(360deg); } }
@keyframes houseFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes houseTyping { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      `}</style>

      {/* ZOOM OVERLAY */}
      {zoomImg && (
        <div onClick={() => setZoomImg(null)} style={{
          position: 'fixed', inset: 0, zIndex: 3000,
          background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'zoom-out', padding: 24,
        }}>
          <img src={zoomImg} alt="" style={{
            maxWidth: '95vw', maxHeight: '90vh', borderRadius: 16,
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} />
        </div>
      )}

      {/* LEAD CAPTURE MODAL */}
      {showLeadCapture && !leadSubmitted && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 24, padding: 40,
            maxWidth: 440, width: '100%', animation: 'houseFadeIn 0.3s ease',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{
                width: 60, height: 60, borderRadius: 16, margin: '0 auto 16px',
                background: 'linear-gradient(135deg, #d4a537, #f0c75e)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28,
              }}>&#127916;</div>
              <h3 style={{ fontSize: 22, fontWeight: 800, color: '#1a1a2e', marginBottom: 8 }}>{t.interested}</h3>
              <p style={{ fontSize: 14, color: '#888' }}>{t.leadCapture}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { key: 'name', ph: t.name, type: 'text' },
                { key: 'email', ph: t.email, type: 'email' },
                { key: 'phone', ph: t.phone, type: 'tel' },
              ].map(f => (
                <input key={f.key} placeholder={f.ph} type={f.type} value={leadForm[f.key]}
                  onChange={e => setLeadForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  style={{
                    padding: '14px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)',
                    fontSize: 14, outline: 'none', background: '#f5f5fa',
                  }}
                  onFocus={e => e.target.style.borderColor = '#d4a537'}
                  onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                />
              ))}
              <button onClick={handleLeadSubmit} style={{
                padding: 16, background: 'linear-gradient(135deg, #e8363a, #c02a2e)', color: '#fff',
                border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 4,
              }}>{t.submit}</button>
              <button onClick={() => setShowLeadCapture(false)} style={{
                padding: 12, background: 'none', border: 'none', color: '#999', fontSize: 14, cursor: 'pointer',
              }}>{t.continue}</button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        height: isMobile ? 'auto' : 'calc(100vh - 72px)',
      }}>

        {/* MAIN TOUR AREA */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Back button */}
          <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={() => navigate(`/h/${slug}/property/${tourId}`)} style={{
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10, padding: '8px 16px', color: 'rgba(255,255,255,0.6)',
              fontSize: 13, cursor: 'pointer', transition: 'all 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
            >
              &#8592; {t.back}
            </button>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
              {currentImages.length > 0 && `${t.photoOf} ${activePhoto + 1}/${currentImages.length}`}
            </span>
          </div>

          {/* Tour Viewer — Real Photos */}
          <div style={{
            flex: 1, margin: isMobile ? '0 16px 16px' : '0 20px 20px',
            borderRadius: 20, overflow: 'hidden', position: 'relative',
            minHeight: isMobile ? 300 : 400,
          }}>
            {/* Image or gradient fallback */}
            {/* Always show gradient as base layer */}
            <div style={{
              width: '100%', height: '100%', position: 'absolute', inset: 0,
              background: FALLBACK_GRADIENTS[activeRoom % FALLBACK_GRADIENTS.length],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 96, opacity: 0.15 }}>{currentRoom?.icon}</span>
            </div>
            {currentImg && (
              <img
                src={currentImg} alt={roomNames[currentRoom?.key] || ''}
                onClick={() => setZoomImg(currentImg)}
                onError={e => { e.target.style.display = 'none'; }}
                style={{
                  width: '100%', height: '100%', objectFit: 'cover',
                  position: 'absolute', inset: 0, cursor: 'zoom-in',
                  transition: 'opacity 0.4s ease',
                }}
              />
            )}

            {/* Dark overlay for readability */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 30%, transparent 60%, rgba(0,0,0,0.5) 100%)',
              pointerEvents: 'none',
            }} />

            {/* Room label */}
            <div style={{
              position: 'absolute', top: 20, left: 20,
              background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(12px)',
              borderRadius: 14, padding: '10px 20px',
              color: '#fff', fontSize: 16, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 10,
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <span style={{ fontSize: 20 }}>{currentRoom?.icon}</span>
              {roomNames[currentRoom?.key] || 'Room'}
            </div>

            {/* Photo sub-navigation dots */}
            {currentImages.length > 1 && (
              <div style={{
                position: 'absolute', top: 20, right: 20,
                display: 'flex', gap: 6,
              }}>
                {currentImages.map((_, i) => (
                  <button key={i} onClick={() => setActivePhoto(i)} style={{
                    width: i === activePhoto ? 24 : 8, height: 8, borderRadius: 4,
                    background: i === activePhoto ? '#d4a537' : 'rgba(255,255,255,0.4)',
                    border: 'none', cursor: 'pointer', transition: 'all 0.3s',
                  }} />
                ))}
              </div>
            )}

            {/* Room navigation arrows */}
            <button onClick={() => setActiveRoom(prev => Math.max(0, prev - 1))} disabled={activeRoom === 0} style={{
              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
              width: 52, height: 52, borderRadius: '50%',
              background: activeRoom === 0 ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 22, cursor: activeRoom === 0 ? 'default' : 'pointer',
              backdropFilter: 'blur(8px)', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={e => { if (activeRoom > 0) e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; }}
            onMouseLeave={e => { if (activeRoom > 0) e.currentTarget.style.background = 'rgba(0,0,0,0.5)'; }}
            >&#8592;</button>

            <button onClick={() => setActiveRoom(prev => Math.min(ROOMS.length - 1, prev + 1))} disabled={activeRoom === ROOMS.length - 1} style={{
              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
              width: 52, height: 52, borderRadius: '50%',
              background: activeRoom === ROOMS.length - 1 ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 22, cursor: activeRoom === ROOMS.length - 1 ? 'default' : 'pointer',
              backdropFilter: 'blur(8px)', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={e => { if (activeRoom < ROOMS.length - 1) e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; }}
            onMouseLeave={e => { if (activeRoom < ROOMS.length - 1) e.currentTarget.style.background = 'rgba(0,0,0,0.5)'; }}
            >&#8594;</button>

            {/* Bottom hint */}
            {currentImg && (
              <div style={{
                position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)',
                borderRadius: 20, padding: '6px 16px', color: 'rgba(255,255,255,0.5)', fontSize: 12,
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                &#128269; {t.clickToZoom}
              </div>
            )}
          </div>

          {/* Room Thumbnails */}
          <div style={{
            padding: isMobile ? '0 16px 16px' : '0 20px 20px',
            display: 'flex', gap: 8, overflowX: 'auto',
          }}>
            {ROOMS.map((room, i) => {
              const imgs = getRoomImages(room.key);
              const thumb = imgs[0] || null;
              return (
                <button key={i} onClick={() => setActiveRoom(i)} style={{
                  flexShrink: 0, width: isMobile ? 80 : 100, height: isMobile ? 64 : 72,
                  borderRadius: 12, border: activeRoom === i ? '2px solid #d4a537' : '2px solid transparent',
                  cursor: 'pointer', overflow: 'hidden', position: 'relative',
                  opacity: activeRoom === i ? 1 : 0.6,
                  transition: 'all 0.2s',
                  background: thumb ? '#222' : FALLBACK_GRADIENTS[i % FALLBACK_GRADIENTS.length],
                }}>
                  <span style={{ fontSize: isMobile ? 18 : 22, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                    {room.icon}
                  </span>
                  {thumb && (
                    <img src={thumb} alt={roomNames[room.key]} onError={e => { e.target.style.display = 'none'; }} style={{
                      width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0,
                    }} />
                  )}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    padding: '12px 4px 4px', textAlign: 'center',
                  }}>
                    <span style={{ fontSize: 9, color: '#fff', fontWeight: 600 }}>
                      {(roomNames[room.key] || '').split(' ')[0]}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Progress Bar */}
          <div style={{ padding: isMobile ? '0 16px 16px' : '0 20px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{t.progress}</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{activeRoom + 1}/{ROOMS.length}</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 2,
                background: 'linear-gradient(90deg, #d4a537, #f0c75e)',
                width: `${progress}%`, transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div style={{
          width: isMobile ? '100%' : 380, flexShrink: 0,
          background: 'rgba(255,255,255,0.03)',
          borderLeft: isMobile ? 'none' : '1px solid rgba(255,255,255,0.06)',
          display: 'flex', flexDirection: 'column',
          maxHeight: isMobile ? 'none' : 'calc(100vh - 72px)',
        }}>
          {/* Property Info */}
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <h3 style={{
              fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 6,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {p.title}
            </h3>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#d4a537', marginBottom: 12 }}>
              {formatPrice(p.price, p.price_currency || 'THB')}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {[
                { v: p.bedrooms, l: t.beds },
                { v: p.bathrooms, l: t.baths },
                { v: p.area_sqm, l: t.sqm },
              ].map((s, i) => (
                <span key={i} style={{
                  background: 'rgba(255,255,255,0.06)', borderRadius: 8,
                  padding: '4px 10px', fontSize: 12, color: 'rgba(255,255,255,0.5)',
                }}>
                  {s.v} {s.l}
                </span>
              ))}
            </div>
          </div>

          {/* Q&A Section */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '16px 20px 8px' }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                &#129302; {t.aiTourGuide}
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {[t.q1, t.q2, t.q3, t.q4].map((q, i) => (
                  <button key={i} onClick={() => sendChat(q)} style={{
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8, padding: '6px 12px', fontSize: 12, color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,165,55,0.1)'; e.currentTarget.style.color = '#d4a537'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Chat Messages */}
            <div style={{
              flex: 1, overflowY: 'auto', padding: '0 20px',
              display: 'flex', flexDirection: 'column', gap: 10,
              minHeight: isMobile ? 200 : 0,
            }}>
              {chatMessages.map((msg, i) => (
                <div key={i} style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  background: msg.role === 'user' ? 'rgba(232,54,58,0.2)' : 'rgba(255,255,255,0.06)',
                  color: msg.role === 'user' ? '#ff9999' : 'rgba(255,255,255,0.7)',
                  borderRadius: 14, padding: '10px 14px',
                  fontSize: 13, lineHeight: 1.6, maxWidth: '85%',
                  animation: 'houseFadeIn 0.3s ease',
                }}>
                  {msg.content}
                </div>
              ))}
              {chatSending && (
                <div style={{ display: 'flex', gap: 4, padding: '8px 0' }}>
                  {[0, 1, 2].map(i => (
                    <span key={i} style={{
                      width: 6, height: 6, borderRadius: '50%', background: '#d4a537',
                      animation: `houseTyping 1s ease ${i * 0.15}s infinite`,
                    }} />
                  ))}
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div style={{ padding: '12px 20px', display: 'flex', gap: 8 }}>
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendChat(chatInput); } }}
                placeholder={t.chatPlaceholder}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.04)',
                  color: '#fff', fontSize: 13, outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(212,165,55,0.4)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
              <button
                onClick={() => sendChat(chatInput)}
                disabled={!chatInput.trim() || chatSending}
                style={{
                  padding: '10px 16px', borderRadius: 10,
                  background: chatInput.trim() ? 'linear-gradient(135deg, #d4a537, #f0c75e)' : 'rgba(255,255,255,0.06)',
                  border: 'none', color: chatInput.trim() ? '#1a1a2e' : 'rgba(255,255,255,0.3)',
                  fontSize: 13, fontWeight: 600, cursor: chatInput.trim() ? 'pointer' : 'default',
                }}
              >
                {t.send}
              </button>
            </div>
          </div>

          {/* CTAs */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <button onClick={() => {}} style={{
              width: '100%', padding: 14,
              background: 'linear-gradient(135deg, #e8363a, #c02a2e)',
              color: '#fff', border: 'none', borderRadius: 12,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 16px rgba(232,54,58,0.3)',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              &#128197; {t.schedule}
            </button>
            <button onClick={() => navigate(`/h/${slug}/property/${tourId}`)} style={{
              width: '100%', padding: 12,
              background: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
            >
              {t.viewProperty}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
